import {clsx} from 'clsx'
import {forwardRef, type HTMLAttributes, memo, type RefObject} from 'react'

import type {TableDataType} from '../../../components/table/table-data-type'
import {getSelectionInfo, isRowActionable} from '../../../components/table/use-row-selection'
import type {Row} from '../../../hooks/tanstack-table/helpers/types'
import {useTableSelectedRowIds} from '../../../hooks/tanstack-table/hooks/use-table-row-selection'
import {useTableInstance} from '../../../hooks/tanstack-table/providers/table-provider'
import {useRoadmapTableWidth} from '../../../hooks/use-roadmap-settings'
import {useRoadmapScrollMargins} from '../hooks/use-roadmap-scroll-margins'
import sharedStyles from './roadmap-shared.module.css'
import styles from './roadmap-table-layout.module.css'

export type TestIdentifiersProps = {
  'data-testid'?: string
}

export function RoadmapCellContent({children, className}: {children: React.ReactNode; className?: string}) {
  return <div className={clsx(className, styles.Box)}>{children}</div>
}

type RoadmapCellProps = {
  ref?: RefObject<HTMLDivElement | null>
  children: React.ReactNode
  role?: string
  style?: React.CSSProperties
  className?: string
} & TestIdentifiersProps

export const RoadmapCell = memo(
  forwardRef<HTMLDivElement, RoadmapCellProps>(function RoadmapCell(props, ref?) {
    const {children, role = 'gridcell', style, ...other} = props

    return (
      <div role={role} style={style} className={styles.Box_1} {...other} ref={ref}>
        {children}
      </div>
    )
  }),
)

type RoadmapRowProps = {
  row?: Row<TableDataType>
  ref?: RefObject<HTMLDivElement | null>
  children?: React.ReactNode
  isSelected?: boolean
  isDraggingPill?: boolean
  isHighlighted?: boolean
} & TestIdentifiersProps &
  HTMLAttributes<HTMLDivElement>

export const RoadmapRow = forwardRef<HTMLDivElement, RoadmapRowProps>(function RoadmapRow(
  {row, children, isDraggingPill, isHighlighted, className: externalClassName, ...props},
  ref?,
) {
  const {flatRows: legacyFlatRows, groupedRows: legacyGroupedRows, getPreExpandedRowModel} = useTableInstance()
  const flatRows = getPreExpandedRowModel ? getPreExpandedRowModel().flatRows : legacyFlatRows
  const groupedRows = getPreExpandedRowModel ? undefined : legacyGroupedRows
  const selectedRowIds = useTableSelectedRowIds()
  const selectionInfo = row ? getSelectionInfo(row, flatRows, groupedRows) : null
  const isSelected = row && Boolean(selectedRowIds?.[row.id]) && isRowActionable(row)

  const tableWidth = useRoadmapTableWidth()
  const className = clsx(
    'roadmap-row',
    {
      'row-highlight': isHighlighted,
      'row-selected': isSelected,
      'selection-top-edge':
        isSelected && selectionInfo && (!selectionInfo.beforeSelected || selectionInfo.firstInGroup),
      'selection-bottom-edge':
        isSelected && selectionInfo && (!selectionInfo.afterSelected || selectionInfo.lastInGroup),
      'pill-drag': isDraggingPill,
    },
    externalClassName,
  )
  const scrollMargins = useRoadmapScrollMargins()

  return (
    <div
      ref={ref}
      role="row"
      className={clsx(className, sharedStyles.Row)}
      style={{...scrollMargins, marginLeft: `-${tableWidth}px`}}
      data-test-row-is-selected={isSelected}
      {...props}
    >
      {children}
    </div>
  )
})

try{ RoadmapCellContent.displayName ||= 'RoadmapCellContent' } catch {}
try{ RoadmapCell.displayName ||= 'RoadmapCell' } catch {}
try{ RoadmapCell.displayName ||= 'RoadmapCell' } catch {}
try{ RoadmapRow.displayName ||= 'RoadmapRow' } catch {}